import { api } from './api';
import {
  AssignConversationRequest,
  Conversation,
  ConversationFilters,
  getConversationConductor,
  HandoffToBotRequest,
  HandoffToHumanRequest,
  UpdateConversationStatusRequest,
} from '../types/conversation';
import { normalizeConversationStatus } from '../types/atendimentoEnums';
import {
  CreateSupportMessageRequest,
  SupportMessage,
} from '../types/supportMessage';
import {
  ConversationActivity,
  ConversationThreadItem,
  ReopenConversationRequest,
} from '../types/conversationActivity';
import { TransferConversationGroupRequest } from '../types/supportGroup';
import {
  ApplyConversationLabelsRequest,
  ConversationLabel,
  RemoveConversationLabelsRequest,
} from '../types/label';

class ConversationService {
  private normalizeConversation(conversation: Conversation): Conversation {
    return {
      ...conversation,
      status: normalizeConversationStatus(conversation.status),
      unreadCount: conversation.unreadCount ?? 0,
    };
  }

  private normalizeConversations(conversations: Conversation[]): Conversation[] {
    return conversations.map((conversation) => this.normalizeConversation(conversation));
  }

  async getAll(filters?: ConversationFilters): Promise<Conversation[]> {
    const params: Record<string, string | number | (string | number)[]> = {};

    if (filters?.inboxId)
    {
      params.inboxId = filters.inboxId;
    }

    if (filters?.status !== undefined)
    {
      params.status = filters.status;
    }

    if (filters?.statuses && filters.statuses.length > 0)
    {
      params.statuses = filters.statuses;
    }

    if (filters?.assigneeId)
    {
      params.assigneeId = filters.assigneeId;
    }

    if (filters?.priority !== undefined)
    {
      params.priority = filters.priority;
    }

    if (filters?.supportGroupId)
    {
      params.supportGroupId = filters.supportGroupId;
    }

    if (filters?.labelId)
    {
      params.labelId = filters.labelId;
    }

    if (filters?.channelType !== undefined)
    {
      params.channelType = filters.channelType;
    }

    if (filters?.teamId)
    {
      params.teamId = filters.teamId;
    }

    if (filters?.search)
    {
      params.search = filters.search;
    }

    const response = await api.get<Conversation[]>('/conversations', {
      params,
      paramsSerializer: {
        indexes: null,
      },
    });
    return this.normalizeConversations(response.data);
  }

  async getById(id: string): Promise<Conversation> {
    const response = await api.get<Conversation>(`/conversations/${id}`);
    return this.normalizeConversation(response.data);
  }

  async getMessages(id: string): Promise<SupportMessage[]> {
    const response = await api.get<SupportMessage[]>(`/conversations/${id}/messages`);
    return response.data;
  }

  async sendMessage(id: string, data: CreateSupportMessageRequest): Promise<SupportMessage> {
    const response = await api.post<SupportMessage>(`/conversations/${id}/messages`, data);
    return response.data;
  }

  async retryMessage(conversationId: string, messageId: string): Promise<SupportMessage> {
    const response = await api.post<SupportMessage>(
      `/conversations/${conversationId}/messages/${messageId}/retry`,
    );
    return response.data;
  }

  async updateStatus(id: string, data: UpdateConversationStatusRequest): Promise<Conversation> {
    const response = await api.put<Conversation>(`/conversations/${id}/status`, data);
    return this.normalizeConversation(response.data);
  }

  async assign(id: string, data: AssignConversationRequest): Promise<Conversation> {
    const response = await api.put<Conversation>(`/conversations/${id}/assign`, data);
    return this.normalizeConversation(response.data);
  }

  async reopen(id: string, data?: ReopenConversationRequest): Promise<Conversation> {
    const response = await api.post<Conversation>(`/conversations/${id}/reopen`, data ?? {});
    return this.normalizeConversation(response.data);
  }

  async getActivities(id: string): Promise<ConversationActivity[]> {
    const response = await api.get<ConversationActivity[]>(`/conversations/${id}/activities`);
    return response.data;
  }

  async getThread(id: string): Promise<ConversationThreadItem[]> {
    const response = await api.get<ConversationThreadItem[]>(`/conversations/${id}/thread`);
    return response.data;
  }

  async transferGroup(id: string, data: TransferConversationGroupRequest): Promise<Conversation> {
    const response = await api.post<Conversation>(`/conversations/${id}/transfer-group`, data);
    return this.normalizeConversation(response.data);
  }

  async getLabels(id: string): Promise<ConversationLabel[]> {
    const response = await api.get<ConversationLabel[]>(`/conversations/${id}/labels`);
    return response.data;
  }

  async applyLabels(id: string, data: ApplyConversationLabelsRequest): Promise<ConversationLabel[]> {
    const response = await api.post<ConversationLabel[]>(`/conversations/${id}/labels`, data);
    return response.data;
  }

  async removeLabels(id: string, data: RemoveConversationLabelsRequest): Promise<void> {
    await api.delete(`/conversations/${id}/labels`, { data });
  }

  async handoffToHuman(id: string, data?: HandoffToHumanRequest): Promise<Conversation> {
    const response = await api.post<Conversation>(`/conversations/${id}/handoff/to-human`, data ?? {});
    return this.normalizeConversation(response.data);
  }

  async handoffToBot(id: string, data?: HandoffToBotRequest): Promise<Conversation> {
    const response = await api.post<Conversation>(`/conversations/${id}/handoff/to-bot`, data ?? {});
    return this.normalizeConversation(response.data);
  }

  async presentForm(id: string, formId: string): Promise<SupportMessage> {
    const response = await api.post<SupportMessage>(`/conversations/${id}/forms/present`, { formId });
    return response.data;
  }

  async assume(id: string): Promise<Conversation> {
    const response = await api.post<Conversation>(`/conversations/${id}/assume`);
    return this.normalizeConversation(response.data);
  }

  async markLastSeen(id: string): Promise<Conversation> {
    const response = await api.post<Conversation>(`/conversations/${id}/last-seen`);
    return this.normalizeConversation(response.data);
  }

  async markUnread(id: string): Promise<Conversation> {
    const response = await api.post<Conversation>(`/conversations/${id}/unread`);
    return this.normalizeConversation(response.data);
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/conversations/${id}`);
  }
}

export const conversationService = new ConversationService();
